if (!customElements.get('quick-add-modal')) {
  class QuickAdd extends HTMLElement {
    constructor() {
      super();
      this.modal = null;
      this.modalContent = null;
      this.setupModal();
      this.bindEvents();
    }

    setupModal() {
      this.modal = this.querySelector('[role="dialog"]');
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      document.body.appendChild(this);
    }

    bindEvents() {
      this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', () => this.hide());
      this.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();

        // Handle reviews link/badge click inside quick view
        const reviewsTarget = event.target.closest('a[href*="#judgeme_product_reviews"], .jdgm-preview-badge, .jdgm-prev-badge');
        if (reviewsTarget && this.openedBy) {
          // Ignore clicks on the "Reviews Summary" toggle
          if (event.target.closest('.reveiws_summary')) return;
          
          event.preventDefault();
          event.stopPropagation();
          const productUrl = this.openedBy.getAttribute('data-product-url').split('?')[0];
          window.location.href = `${productUrl}#judgeme_product_reviews`;
        }
      });
      this.bindStickySizeGuideTabs();
      this.bindOverlaySizeGuideTabs();

    }

    bindStickySizeGuideTabs() {
      if (!this.modalContent) return;

      this.modalContent.addEventListener('click', (event) => {
        const clickedTab = event.target.closest('.sticky-mobile-tabs .sizes-tab, .sticky-mobile-tabs .size-guide-tab');
        if (!clickedTab) return;

        const tabsContainer = clickedTab.closest('.sticky-mobile-tabs');
        const mobileWrapper = clickedTab.closest('.sticky-mobile-view-wrapper') || this.modalContent;
        if (!tabsContainer) return;

        const sizesTab = tabsContainer.querySelector('.sizes-tab');
        const sizeGuideTab = tabsContainer.querySelector('.size-guide-tab');
        const sizesContent = mobileWrapper.querySelector('.sticky-variant-accordion.sizes-tab');
        const sizeGuideContent = mobileWrapper.querySelector('.sticky-mobile-size-guide.size-guide-tab');

        if (clickedTab.classList.contains('sizes-tab')) {
          sizesTab?.classList.add('active');
          sizeGuideTab?.classList.remove('active');
          if (sizesContent) {
            sizesContent.style.display = '';
            sizesContent.style.visibility = 'visible';
          }
          if (sizeGuideContent) {
            sizeGuideContent.style.display = 'none';
            sizeGuideContent.style.visibility = 'hidden';
          }
          return;
        }

        sizeGuideTab?.classList.add('active');
        sizesTab?.classList.remove('active');
        if (sizeGuideContent) {
          sizeGuideContent.style.display = '';
          sizeGuideContent.style.visibility = 'visible';
        }
        if (sizesContent) {
          sizesContent.style.display = 'none';
          sizesContent.style.visibility = 'hidden';
        }
      }, true);
    }

    bindOverlaySizeGuideTabs() {
      if (!this.modalContent) return;

      this.modalContent.addEventListener('click', (event) => {
        const clickedTab = event.target.closest('.size-guide-overlay .size-guide-tabs .size-guide-tab');
        if (!clickedTab) return;

        event.preventDefault();
        event.stopPropagation();

        const overlay = clickedTab.closest('.size-guide-overlay');
        const targetTab = clickedTab.getAttribute('data-tab');
        if (!overlay || !targetTab) return;

        const tabs = overlay.querySelectorAll('.size-guide-tabs .size-guide-tab');
        const contents = overlay.querySelectorAll('.size-guide-tab-content');
        const targetContent = overlay.querySelector(`.size-guide-tab-content[data-content="${targetTab}"]`);

        tabs.forEach((tab) => tab.classList.remove('active'));
        contents.forEach((content) => content.classList.remove('active'));

        clickedTab.classList.add('active');
        targetContent?.classList.add('active');
      }, true);
    }

    show(opener) {
      this.openedBy = opener;

      // Only set aria-disabled and show spinner if it's a quick-add operation
      // with a loading spinner element
      if (opener && opener.querySelector('.loading__spinner')) {
        opener.setAttribute('aria-disabled', true);
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then(response => response.text())
          .then(responseText => {
            const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = parsedDocument.querySelector('product-info');
            if (!productElement) return;

            const templateSuffixClass = Array.from(parsedDocument.body?.classList || []).find(
              (className) => className.startsWith('product-template-suffix-')
            );

            if (templateSuffixClass) {
              const templateSuffix = templateSuffixClass.replace('product-template-suffix-', '');
              if (templateSuffix) {
                productElement.setAttribute('data-product-template-suffix', templateSuffix);
              }
            }

            productElement.setAttribute('data-update-url', 'false');
            productElement.setAttribute('data-quick-add-context', 'true');

            this.preprocessContent(productElement);

            const productUrl = opener.getAttribute('data-product-url').split('?')[0];
            const reviewLinks = productElement.querySelectorAll('a[href="#judgeme_product_reviews"]');
            reviewLinks.forEach(link => {
              link.setAttribute('href', `${productUrl}#judgeme_product_reviews`);
            });

            this.setContent(productElement.outerHTML);
            this.setAttribute('data-has-quick-add-content', 'true');

            document.body.classList.add('overflow-hidden');
            this.setAttribute('open', '');

            if (window.Shopify?.PaymentButton) Shopify.PaymentButton.init();
            if (window.ProductModel) window.ProductModel.loadShopifyXR();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      } else {
        // For other modals (like monogram popup) that don't need fetch
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
      }
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      this.removeAttribute('data-has-quick-add-content');
      this.modalContent.innerHTML = '';
    }

    preprocessContent(element) {
      const newId = `${element.dataset.section}`;
      element.innerHTML = element.innerHTML.replaceAll(element.dataset.section, newId);
      element.setAttribute('data-update-url', 'false');
    }

    setContent(html) {
      this.modalContent.innerHTML = html;
      // Reinject scripts
      this.modalContent.querySelectorAll('script').forEach(oldScript => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }

  }

  customElements.define('quick-add-modal', QuickAdd);
}


function isDomElement(node) {
  return Boolean(node && typeof node === 'object' && node.nodeType === 1);
}

function getQuickAddModalForRequest(requestState) {
  const initiator = requestState?.info?.initiator;
  const openModals = Array.from(
    document.querySelectorAll('quick-add-modal[open][data-has-quick-add-content]')
  );

  if (!openModals.length) return null;

  if (isDomElement(initiator)) {
    for (const modal of openModals) {
      if (modal.contains(initiator)) return modal;

      const productInfo = initiator.closest?.('product-info[data-quick-add-context]');
      if (productInfo && modal.contains(productInfo)) return modal;
    }

    return null;
  }

  return openModals[0] || null;
}

function onCartUpdate(e) {
  try {
    const { requestState, cart, previousCart } = e?.detail || {};

    if (!requestState) return;

    if (requestState.requestType === 'add' && requestState.responseData?.ok) {
      const quickAddModal = getQuickAddModalForRequest(requestState);

      document.body.classList.add('js-show-ajax-cart');
      document.body.classList.remove('overflow-hidden');

      document.querySelectorAll('quick-add-modal').forEach((modal) => {
        modal.removeAttribute('open');
        modal.removeAttribute('data-has-quick-add-content');
        modal.modalContent.innerHTML = '';
      });

      document.dispatchEvent(
        new CustomEvent('item-added-to-cart', {
          detail: requestState?.responseData?.body || null,
        })
      );

      if (quickAddModal) {
        window.__warroadTrackQuickAddCart?.(requestState, cart, previousCart);

        document.dispatchEvent(
          new CustomEvent('quick-add-modal:added-to-cart', {
            detail: {
              requestState,
              cart,
              previousCart,
              modal: quickAddModal,
            },
          })
        );
      }
    }
  } catch (error) {
    console.error('Error handling cart update:', error);
  }
}

if (!window.__liquidAjaxCartQuickAddListener) {
  window.__liquidAjaxCartQuickAddListener = true;
  document.addEventListener('liquid-ajax-cart:request-end', onCartUpdate);
}
